import React, { useState } from 'react'
import { Nav, Input, InputGroup, Tooltip, Whisper } from 'rsuite'
import PlusIcon from '@rsuite/icons/Plus'
import { Search } from '@rsuite/icons'
import { Button } from '../../atoms'
import './toolbar.scss'

interface Props {
  options: { label: string, value: string, onClick: () => void }[];
  buttonName: string;
  onButtonClick: () => void;
  total: number;
  onSearchChange: (value: string) => void;
  showButton?: boolean;
  rightExtra?: React.ReactNode;
  defaultActive?: string;
  /** Greys out the create button while its precondition isn't met. */
  buttonDisabled?: boolean;
  /** Hover tooltip on the create button — use it to say *why* it's disabled. */
  buttonTooltip?: string;
}

const Toolbar: React.FC<Props> = ({
  options,
  buttonName,
  onButtonClick,
  total,
  onSearchChange,
  showButton = true,
  rightExtra,
  defaultActive,
  buttonDisabled = false,
  buttonTooltip
}) => {
  const [active, setActive] = useState(defaultActive ?? options[0]?.value)

  const onNavSelect = (
    activeKey: string,
    option: { label: string, value: string, onClick: () => void }
  ) => {
    setActive(activeKey)
    option.onClick()
  }

  return (
    <div className="toolbar">
      <div className="toolbar-left">
        {options.length > 0 && (
          <Nav
            appearance="subtle"
            activeKey={active}
            style={{ marginRight: 10 }}
          >
            {options.map((option) => (
              <Nav.Item
                key={option.value}
                role="tab"
                eventKey={option.value}
                onClick={() => onNavSelect(option.value, option)}
              >
                {option.label}
              </Nav.Item>
            ))}
          </Nav>
        )}
        <div className="toolbar-totals">
          <span>Total: {total}</span>
        </div>
      </div>
      <div className="toolbar-right-pane">
        {rightExtra}
        <div>
          <InputGroup size="md" style={{ width: 300, margin: 10 }}>
            <InputGroup.Addon>
              <Search />
            </InputGroup.Addon>
            <Input
              placeholder="Search..."
              role="search"
              onChange={(value: string) => onSearchChange(value)}
            />
          </InputGroup>
        </div>

        {showButton && (
          <Whisper
            placement="bottomEnd"
            trigger={buttonTooltip ? 'hover' : 'none'}
            speaker={<Tooltip>{buttonTooltip}</Tooltip>}
            container={() => document.body}
            delayOpen={150}
          >
            {/* Wrapped in a span so the hover trigger still fires while the
                button is `disabled` — a disabled <button> swallows pointer
                events, which is exactly when the tooltip matters most. */}
            <span style={{ display: 'inline-flex' }}>
              <Button
                className="toolbar-btn"
                appearance="primary"
                startIcon={<PlusIcon />}
                disabled={buttonDisabled}
                onClick={onButtonClick}
              >
                {buttonName}
              </Button>
            </span>
          </Whisper>
        )}
      </div>
    </div>
  )
}

export default Toolbar
